import debounce from 'lodash/debounce';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Form, Input, message, Row, Select, Typography, UploadFile as UploadFileType } from 'antd';
import { Flex, AppButton, UploadFile, RetryAlert, PageContainer } from '@/components';
import { useDevice } from '@/hooks';
import { useHelpdesk } from '@/hooks/app';
import { HelpdeskHttp } from '@/services';
import { HelpdeskValidation } from '@/validations';
import { ReactComponent as IcSupport } from '@/assets/images/ic-support.svg';
import formErrors from '@/utils/formErrors';
import { Routes } from '@/routes';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

export default function HelpdeskCreateIssue() {

  const {
    typesLoading,
    createIssue,
    issuesTypes,
    fetchIssuesTypes,
    categoryList,
    categoryError,
    categoryLoading,
    fetchCategories,
    typesError
  } = useHelpdesk();

  const [t] = useTranslation(['common', 'helpdesk']);

  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const [type, setType] = useState<string>(null);
  const [files, setFiles] = useState<UploadFileType[]>([]);
  const [category, setCategory] = useState<string>(undefined);
  const [createLoading, setCreateLoading] = useState(false);
  const [submitTried, trySubmit] = useState(false);

  const boxDescription = useMemo(() => {
    return {
      long: t('helpdesk:messages.new-request-help-long'),
      short: t('helpdesk:messages.new-request-help-short')
    };
  }, [t]);

  const formik = useFormik({
    validationSchema: HelpdeskValidation.createIssue,
    initialValues: {
      subject: '',
      description: '',
    },
    onSubmit: async (values) => {
      setCreateLoading(true);
      const { subject, description } = values;

      const uploads = [];
      for (const file of files) {
        try {
          const response = (await HelpdeskHttp.uploadAttachment(file as any));
          const { upload } = response;
          uploads.push({
            name: upload.name,
            token: upload.token,
            content_type: upload.type
          });
        }
        catch (e) {
          message.error({ duration: 3, content: t('helpdesk:messages.fail-upload') });
          setCreateLoading(false);
          return;
        }
      }

      try {
        const payload = { subject, description, categoryId: category, uploads };
        const response = await createIssue(payload);
        if (response?.issue?.id) {
          message.success({ content: t('helpdesk:messages.success-issue') });
          navigate(Routes.HelpdeskViewIssue);
          return;
        }
      }
      catch (e) {
        message.error({ duration: 3, content: t('helpdesk:messages.fail-issue') });
      }

      setCreateLoading(false);
    }
  });

  // Fetch issues with component load
  useEffect(() => {
    fetchIssuesTypes();
  }, []);

  // Make sure reset category after changing type
  useEffect(() => {
    setCategory(null);
    if (!type) return;
    fetchCategories(type);
  }, [type]);

  const handleAddFile = useCallback((file: UploadFileType) => {
    setFiles(files => [...files, file]);
  }, []);

  const handleRemove = useCallback(async (file: UploadFileType) => {
    setFiles(files => files.filter(f => f.uid !== file.uid));
  }, []);

  const handleSubmitIssue = useCallback(() => {
    trySubmit(true);
    formik.submitForm();
  }, [formik]);

  return (
    <PageContainer title={t('helpdesk:labels.new-request')}>
      <Row gutter={[16, 16]}>
        {typesError ? <RetryAlert noIcon content={typesError} action={fetchIssuesTypes} /> :
          <>
            <Col md={7} xs={24}>
              <Alert
                type="warning"
                className="text-sm md:p-4 p-3 leading-relaxed text-justify bg-[#FFF3D3]"
                message={(
                  <div className="text-[#b08712] font-normal flex flex-col">
                    <div className="relative flex md:flex-col flex-row items-center">
                      <IcSupport className={`${isMobile() ? 'w-[50px] pb-0' : 'w-[60px] pb-3'} mx-auto`} />
                      <div className="md:ps-0 ps-3">
                        {isMobile() ? boxDescription.short : boxDescription.long}
                      </div>
                    </div>
                  </div>
                )}
              />
            </Col>
            <Col md={10} xs={24}>
              <Row gutter={[16, 16]}>
                <Col lg={12} span={24}>
                  <Select
                    size="large"
                    className="w-full"
                    disabled={typesLoading}
                    loading={typesLoading}
                    value={type}
                    placeholder={t('helpdesk:messages.select-request-type')}
                    onChange={val => setType(val)}
                  >
                    {issuesTypes.map((type) => (
                      <Option
                        key={type?.value}
                        value={type?.value}>
                        {type?.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col lg={12} span={24}>
                  <Select
                    disabled={!type || categoryLoading || !!categoryError}
                    size="large"
                    value={category}
                    className="w-full"
                    loading={categoryLoading}
                    placeholder={t('helpdesk:messages.select-category')}
                    onChange={val => setCategory(val)}
                  >
                    {categoryList.map((type) => (
                      <Option
                        key={type?.id}
                        value={type?.id}>
                        {type?.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                {categoryError &&
                  <Col span={24}>
                    <Typography.Text className="text-sm" type="danger">{categoryError}</Typography.Text>
                  </Col>
                }
                <Col span={24}>
                  <Flex gap={4} direction="column">
                    <Form.Item {...formErrors(formik.errors.subject, submitTried)}>
                      <Input
                        size="large"
                        name="subject"
                        placeholder={t('common:labels.title')}
                        onChange={debounce(formik.handleChange, 500)}
                      />
                    </Form.Item>
                    <Form.Item {...formErrors(formik.errors.description, submitTried)}>
                      <Input.TextArea
                        rows={7}
                        name="description"
                        size="large"
                        placeholder={t('common:labels.description')}
                        onChange={debounce(formik.handleChange, 500)}
                        style={{ maxHeight: '300px' }}
                      />
                    </Form.Item>
                  </Flex>
                </Col>
              </Row>
            </Col >
            <Col md={7} xs={24}>
              <Flex gap={16} className="helpdesk-create-upload-wrapper" direction="column">
                <UploadFile
                  style={{ height: 'calc(100% - 120px)' }}
                  iconProps={{ style: { color: '#DEDCDC' } }}
                  onChange={handleAddFile as any}
                  onRemove={handleRemove as any}
                />
                <AppButton
                  size="large"
                  type="primary"
                  appVariant="helpdesk"
                  className="font-bold"
                  loading={createLoading}
                  disabled={!type || !category}
                  onClick={handleSubmitIssue}
                >
                  {t('common:labels.send')}
                </AppButton>
              </Flex>
            </Col>
          </>
        }
      </Row>
    </PageContainer>
  );
}
